import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getIncomingOrders, getMyListings, getSellerStats } from '../services/api';

type NotificationType = 'order' | 'review' | 'low_stock' | 'system' | 'payment';

interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  createdAt: Date;
  timeDisplay: string;
  group: 'Today' | 'Yesterday' | 'Older';
  read: boolean;
}

const TYPE_CONFIG: Record<NotificationType, { icon: string; color: string; bg: string; action: string; path?: string }> = {
  order: { icon: '📦', color: 'text-teal-600', bg: 'bg-teal-50', action: 'View →', path: '/orders' },
  review: { icon: '⭐', color: 'text-amber-500', bg: 'bg-amber-50', action: 'View →', path: '/reviews' },
  low_stock: { icon: '📚', color: 'text-coral-500', bg: 'bg-coral-50', action: 'Edit →', path: '/catalogue' },
  system: { icon: '🎉', color: 'text-purple-600', bg: 'bg-purple-50', action: '', path: '' },
  payment: { icon: '💳', color: 'text-emerald-600', bg: 'bg-emerald-50', action: 'View →', path: '/analytics' },
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'All' | 'Orders' | 'Reviews' | 'System'>('All');
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      // Try fetching real notifications if backend natively supports it
      const res = await api.get('/notifications/my');
      const data = res.data ?? res;
      // if it exists, use it (we assume a structure here, or it fails to catch block if 404)
      if (Array.isArray(data)) {
        // Map backend to local structure
        const mapped = data.map((n: any) => formatBackendNotification(n));
        setNotifications(mapped);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Fallback: Smart Mock Data based on real seller data
        await generateSmartMockData();
      }
    } finally {
      setLoading(false);
    }
  };

  const formatBackendNotification = (n: any): NotificationItem => {
    const date = new Date(n.created_at || new Date());
    return {
      id: n.id || Math.random().toString(),
      type: n.type || 'system',
      title: n.title || 'Notification',
      message: n.body || n.message || '',
      createdAt: date,
      timeDisplay: getTimeDisplay(date),
      group: getGroup(date),
      read: n.read ?? false,
    };
  };

  const generateSmartMockData = async () => {
    try {
      const [ordersRes, statsRes, listingsRes] = await Promise.all([
        getIncomingOrders().catch(() => []),
        getSellerStats().catch(() => null),
        getMyListings().catch(() => [])
      ]);

      const mockData: NotificationItem[] = [];
      const now = new Date();

      // 1. Orders
      if (Array.isArray(ordersRes)) {
        ordersRes.forEach((order: any, idx: number) => {
          const date = new Date(order.created_at);
          // Just so they are nicely distributed in mock
          if (idx === 0) date.setHours(now.getHours() - 2);
          if (idx === 1) date.setDate(now.getDate() - 1);
          
          mockData.push({
            id: `ord_${order.order_id}`,
            type: 'order',
            title: 'New Order',
            message: `"${order.buyer_name || 'Someone'} bought ${order.listing_title} for Rs.${order.price_lkr}"`,
            createdAt: date,
            timeDisplay: getTimeDisplay(date),
            group: getGroup(date),
            read: idx > 1, // first 2 unread
          });
        });
      }

      // 2. Reviews
      if (statsRes && statsRes.rating_count > 0) {
        const reviewDate = new Date();
        reviewDate.setHours(reviewDate.getHours() - 5);
        mockData.push({
          id: 'rev_1',
          type: 'review',
          title: 'New Review',
          message: `"You received a new ${statsRes.rating}★ rating!"`,
          createdAt: reviewDate,
          timeDisplay: getTimeDisplay(reviewDate),
          group: getGroup(reviewDate),
          read: false,
        });
      }

      // 3. Low Stock
      if (Array.isArray(listingsRes)) {
        listingsRes.forEach((listing: any, idx: number) => {
          if (listing.stock_quantity <= 1 && listing.status === 'active') {
            const lsDate = new Date();
            lsDate.setDate(lsDate.getDate() - 1);
            mockData.push({
              id: `ls_${listing.id}`,
              type: 'low_stock',
              title: 'Low Stock Alert',
              message: `"${listing.title} — ${listing.stock_quantity} copy left"`,
              createdAt: lsDate,
              timeDisplay: getTimeDisplay(lsDate),
              group: getGroup(lsDate),
              read: true,
            });
          }
        });
      }

      // 4. System Message (Always)
      const sysDate = new Date();
      sysDate.setDate(sysDate.getDate() - 18);
      mockData.push({
        id: 'sys_1',
        type: 'system',
        title: 'System',
        message: '"Welcome to Readora Seller Hub!"',
        createdAt: sysDate,
        timeDisplay: getTimeDisplay(sysDate),
        group: getGroup(sysDate),
        read: true,
      });

      // Sort by date DESC
      mockData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setNotifications(mockData);
    } catch (e) {
      console.error("Failed to generate mock notifications", e);
    }
  };

  const getTimeDisplay = (date: Date) => {
    const diffMs = new Date().getTime() - date.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHrs / 24);

    if (diffHrs < 1) return 'Just now';
    if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const getGroup = (date: Date): 'Today' | 'Yesterday' | 'Older' => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date >= today) return 'Today';
    if (date >= yesterday) return 'Yesterday';
    return 'Older';
  };

  const handleMarkAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setToast("All notifications marked as read");
    setTimeout(() => setToast(null), 3000);
  };

  const filteredNotifications = useMemo(() => {
    if (filter === 'All') return notifications;
    if (filter === 'Orders') return notifications.filter(n => n.type === 'order');
    if (filter === 'Reviews') return notifications.filter(n => n.type === 'review');
    if (filter === 'System') return notifications.filter(n => ['system', 'low_stock'].includes(n.type));
    return notifications;
  }, [notifications, filter]);

  const grouped = useMemo(() => {
    const groups: Record<string, NotificationItem[]> = {
      Today: [],
      Yesterday: [],
      Older: []
    };
    filteredNotifications.forEach(n => {
      groups[n.group].push(n);
    });
    return groups;
  }, [filteredNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="p-4 lg:p-8 space-y-4">
        <div className="flex justify-between items-center bg-white p-6 rounded-2xl">
          <div className="h-8 w-48 bg-cream-200 rounded animate-pulse" />
          <div className="h-8 w-32 bg-cream-200 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
      
      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-teal-900 text-white px-6 py-3 rounded-xl shadow-xl flex items-center justify-between text-sm font-medium z-50 animate-slide-up">
          <div className="flex items-center gap-3">
            <span className="text-emerald-400">✓</span> {toast}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-3xl shadow-sm border border-cream-200">
        <div>
          <h1 className="text-2xl font-bold text-teal-900 flex items-center gap-2">
            🔔 Notifications
          </h1>
          <p className="text-gray-500 mt-1">
            {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleMarkAllRead}
          disabled={unreadCount === 0}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            unreadCount > 0 
              ? 'bg-cream-100 text-teal-900 hover:bg-cream-200' 
              : 'bg-cream-50 text-gray-400 cursor-not-allowed'
          }`}
        >
          Mark All Read
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
        {['All', 'Orders', 'Reviews', 'System'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f as any)}
            className={`px-5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f 
                ? 'bg-teal text-white shadow-md shadow-teal/20' 
                : 'bg-white text-gray-600 hover:bg-cream-50 border border-cream-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-8">
        {(['Today', 'Yesterday', 'Older'] as const).map(group => {
          const items = grouped[group];
          if (items.length === 0) return null;

          return (
            <div key={group} className="animate-fade-in">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2">
                {group}
              </h2>
              <div className="space-y-3">
                {items.map(item => {
                  const conf = TYPE_CONFIG[item.type];
                  return (
                    <div 
                      key={item.id}
                      className={`relative flex items-start gap-4 p-4 pr-16 sm:pr-24 rounded-2xl border transition-all ${
                        item.read 
                          ? 'bg-white border-cream-200' 
                          : 'bg-blue-50/30 border-blue-100 hover:bg-blue-50/50'
                      }`}
                    >
                      {/* Unread Dot */}
                      {!item.read && (
                        <div className="absolute top-1/2 -translate-y-1/2 left-0 -ml-1.5 w-3 h-3 bg-blue-500 rounded-full shadow-sm ring-4 ring-white" />
                      )}

                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${conf.bg} border border-white shadow-sm`}>
                         {conf.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                          {item.title}
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-white border border-cream-200 ${conf.color}`}>
                            {item.type.replace('_', ' ')}
                          </span>
                        </h3>
                        <p className="text-gray-600 text-sm mt-1 mb-2 leading-relaxed">
                          {item.message}
                        </p>
                        <div className="flex items-center gap-4 text-xs font-medium">
                          <span className="text-gray-400">{item.timeDisplay}</span>
                          
                          {conf.action && (
                            <button
                              onClick={() => navigate(conf.path!)}
                              className={`hover:underline flex items-center gap-1 ${conf.color}`}
                            >
                              {conf.action}
                            </button>
                          )}
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filteredNotifications.length === 0 && (
          <div className="text-center py-16 bg-white rounded-3xl border border-cream-200">
            <div className="w-16 h-16 bg-cream-100 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4">
              📭
            </div>
            <h3 className="text-gray-900 font-bold mb-1">No notifications found</h3>
            <p className="text-gray-500 text-sm">
              You're all caught up!
            </p>
          </div>
        )}
      </div>

    </div>
  );
};

export default Notifications;
